import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Main as Container } from 'Components/Main';
import { PageLoaderList } from 'Components/PageLoader/List';
import { getGapAnalysisReport, deleteGapAnalysisReport } from 'services/gap-analysis.js';
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import { NoData } from 'Components/NoData/index.jsx';
import { PageHelper } from 'Components/PageHelper';
import { Dialog, PageHeader } from 'Components/ui';

const BLUE = 'var(--vo-celeste)';

const AddBtn = styled.button`
  height: 40px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
`;

const Banner = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  border-left: 4px solid var(--vo-celeste);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
`;

const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 12px 16px;
  text-align: ${p => p.$center ? 'center' : 'left'};
  border-bottom: 1px solid var(--vo-cloud);
  background: var(--vo-mist);
  white-space: nowrap;
`;

const Row = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  transition: background .12s;
  &:last-child { border-bottom: none; }
  &:hover { background: var(--vo-capri-25); }
`;

const TdName = styled.td`
  padding: 13px 16px;
  font-size: 14px;
  color: var(--vo-ink);
  font-weight: 500;
`;

const TdSub = styled.td`
  padding: 13px 16px;
  font-size: 13px;
  color: var(--vo-ink-2);
`;

const TdMuted = styled.td`
  padding: 13px 16px;
  font-size: 13px;
  color: var(--vo-slate);
`;

const TdActions = styled.td`
  padding: 13px 16px;
  text-align: center;
`;

const ActionsWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

const ActionBtn = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--vo-slate);
  transition: background .15s, color .15s;
  &:hover {
    background: ${p => p.$danger ? 'var(--vo-danger-10)' : 'var(--vo-info-10)'};
    color: ${p => p.$danger ? 'var(--vo-danger)' : 'var(--vo-celeste)'};
  }
`;

const LOCALE_MAP = { pt: 'pt-BR', en: 'en-US', es: 'es-ES' };

const GapAnalysisReports = () => {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);
  const [open, setOpen] = useState(false);
  const [reFetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const { isLoading, error, data: reportData } = useQuery(
    ['getGapAnalysisReport', selectedTenant.slug, reFetch],
    async () => {
      if (!selectedTenant.slug) return { data: [] };
      const result = await getGapAnalysisReport(selectedTenant.slug);
      return result.data;
    },
  );

  const onDelete = item => { setItemToDelete(item); setOpen(true); };
  const onClose  = () => { setItemToDelete(null); setOpen(false); };

  const onConfirm = async () => {
    try {
      const result = await deleteGapAnalysisReport(itemToDelete?._id);
      if (result.status === 204 || result.status === 200) {
        onClose();
        setRefetch(!reFetch);
      } else {
        throw new Error(t('dashboard:gapAnalysis.error'));
      }
    } catch (err) {
      Dialog.alert({
        title: t('dashboard:alert.errorTitle'),
        message: err?.response?.data?.message ?? err.message,
        icon: 'error',
      });
    }
  };

  const fmtDate = iso => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(LOCALE_MAP[i18n.language] ?? 'pt-BR');
  };

  return (
    <Container>
      <PageHeader
        data-tour="page-title"
        title={t('dashboard:gapAnalysis.title')}
        segments={[
          { label: t('common:breadcrumbs.reports') },
          { label: t('dashboard:gapAnalysis.breadcrumbLabel') },
        ]}
        actions={
          <AddBtn data-tour="add-button" onClick={() => navigate('/reports/gap-analysis/add')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            {t('dashboard:gapAnalysis.newReport')}
          </AddBtn>
        }
      />

      {isLoading && <PageLoaderList />}
      {error && <div>{t('dashboard:gapAnalysis.error')}</div>}

      {!isLoading && !error && (
        <>
          <Banner>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="var(--vo-celeste)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 style={{ flexShrink: 0, marginTop: 1 }}>
              <line x1="18" y1="20" x2="18" y2="10"/>
              <line x1="12" y1="20" x2="12" y2="4"/>
              <line x1="6"  y1="20" x2="6"  y2="14"/>
            </svg>
            <div>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--vo-ink)', margin: '0 0 2px' }}>
                {t('dashboard:gapAnalysis.banner.title')}
              </p>
              <p style={{ fontSize: 13, color: 'var(--vo-slate)', margin: 0, lineHeight: 1.55 }}>
                {t('dashboard:gapAnalysis.banner.description')}
              </p>
            </div>
          </Banner>

          {reportData?.length === 0 && (
            <NoData description={t('dashboard:gapAnalysis.noData')} />
          )}

          {reportData?.length > 0 && (
            <div data-tour="table">
              <TableCard>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('common:table.name')}</Th>
                      <Th>{t('dashboard:gapAnalysis.table.gapAnalysis')}</Th>
                      <Th>{t('dashboard:gapAnalysis.table.generatedAt')}</Th>
                      <Th $center>{t('common:table.actions')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map(item => (
                      <Row key={item._id}>
                        <TdName>{item.name}</TdName>
                        <TdSub>{item.gapAnalysisName}</TdSub>
                        <TdMuted>{fmtDate(item.created_at)}</TdMuted>
                        <TdActions>
                          <ActionsWrap>
                            <ActionBtn
                              title={t('common:buttons.view')}
                              onClick={() => navigate(`/reports/gap-analysis/show/${item._id}`)}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                   stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                              </svg>
                            </ActionBtn>
                            <ActionBtn
                              title={t('common:buttons.delete')}
                              $danger
                              onClick={() => onDelete(item)}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                   stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                              </svg>
                            </ActionBtn>
                          </ActionsWrap>
                        </TdActions>
                      </Row>
                    ))}
                  </tbody>
                </Table>
              </TableCard>
            </div>
          )}
        </>
      )}

      <Dialog
        open={open}
        onConfirm={onConfirm}
        onCancel={onClose}
        message={t('dashboard:gapAnalysis.delete.confirm', { name: itemToDelete?.name })}
      />
      <PageHelper steps={tourSteps} tourKey="reports-gap-analysis" />
    </Container>
  );
};

const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo aos Relatórios de Gap Analysis! Aqui você visualiza todos os relatórios de análise de lacunas de conformidade gerados.',
    disableBeacon: true,
    disableScrolling: true,
    placement: 'center',
  },
  {
    target: '[data-tour="page-title"]',
    content: 'Clique no título para voltar à página anterior.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="add-button"]',
    content: 'Use o botão "Adicionar" para gerar um novo relatório de Gap Analysis e avaliar a conformidade da sua organização.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="table"]',
    content: 'Esta tabela lista todos os relatórios de Gap Analysis. Clique em um relatório para visualizar detalhes completos do dashboard de análise.',
    disableScrolling: false,
  },
  {
    target: 'body',
    content: 'Tour concluído! Gere relatórios de Gap Analysis regularmente para monitorar o progresso da conformidade da sua organização.',
    disableScrolling: true,
    placement: 'center',
  },
];

export default GapAnalysisReports;
